import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import InfoIconButton from '../components/InfoIconButton';
import { useReportsHubNavigation } from '../contexts/ReportsHubNavigationContext';
import { reportsAPI } from '../services/api';
import './TerminationCanonicalReport.css';

function formatDateShort(str) {
  if (!str) return '—';
  const s = String(str).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
  try {
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  } catch (_) {}
  return str;
}

function readSavedState(storageKey, tenantCode) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if ((parsed.tenantCode || null) !== (tenantCode || null)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

const REFERRAL_DATE_PRESET_LABELS = {
  last_month: 'Last month',
  last_quarter: 'Last quarter',
  last_six_months: 'Last six months',
  last_year: 'Last year',
  custom: 'Custom',
};

function summarizeFilterValue(column, value) {
  if (!column || !value) return null;
  const kind = column.filter?.kind;
  if (kind === 'textContains') {
    const text = (value.text || '').trim();
    return text || null;
  }
  if (kind === 'dateRange') {
    const from = value.from || '';
    const to = value.to || '';
    if (!from && !to) return null;
    return `${from ? formatDateShort(from) : 'Any'} to ${to ? formatDateShort(to) : 'Any'}`;
  }
  if (kind === 'dateRangePresets') {
    const preset = (value.preset || '').trim();
    if (preset && preset.toLowerCase() !== 'custom') {
      return REFERRAL_DATE_PRESET_LABELS[preset] || preset;
    }
    if (preset === 'custom' || value.from || value.to) {
      const from = value.from || '';
      const to = value.to || '';
      if (from || to) return `${from ? formatDateShort(from) : 'Any'} to ${to ? formatDateShort(to) : 'Any'}`;
      return 'Custom';
    }
    return null;
  }
  if (kind === 'multiSelect') {
    const values = Array.isArray(value.values) ? value.values : [];
    if (values.length === 0) return null;
    return values.join(', ');
  }
  if (kind === 'boolean') {
    return value?.value === true ? column.label : null;
  }
  return null;
}

function isFilterActive(column, value) {
  return Boolean(summarizeFilterValue(column, value));
}

function TerminationCanonicalReport({ reportId, tenantCode = null, refreshKey = 0, embedded = false }) {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const reportsHubNav = useReportsHubNavigation();
  const storageKey = `canonical-report:${reportId}:state`;
  const [definition, setDefinition] = useState(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState([]);
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({});
  const [total, setTotal] = useState(0);
  const [aggregateTotal, setAggregateTotal] = useState(null);
  const [runAt, setRunAt] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [draftFilters, setDraftFilters] = useState({});
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 190 });
  const [bccMessage, setBccMessage] = useState(null);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  /** Scripts – Pending Receipt: expanded rows show task + work type (main table stays narrow). */
  const [expandedScriptTaskIds, setExpandedScriptTaskIds] = useState(() => new Set());
  const filterButtonRefs = useRef({});
  const isScriptPending = reportId === 'script-tasks-pending';

  const columns = definition?.columns || [];
  const filtersAboveTable = useMemo(() => definition?.filtersAboveTable || [], [definition]);
  const visibleColumns = useMemo(() => {
    const above = new Set(filtersAboveTable);
    return columns.filter((c) => {
      if (c.visibleByDefault === false) return false;
      if (above.has(c.key) && (c.key === 'due_by' || c.key === 'show_due_only')) return false;
      return true;
    });
  }, [columns, filtersAboveTable]);

  const numericColumnKeys = useMemo(() => {
    const s = new Set(['count', 'intake', 'ifsp', 'pct_total']);
    if (isScriptPending) s.add('days_pending');
    return s;
  }, [isScriptPending]);

  const scriptPendingColSpan = visibleColumns.length + (isScriptPending ? 1 : 0);

  useEffect(() => {
    setDescriptionOpen(false);
  }, [reportId]);

  useEffect(() => {
    let mounted = true;
    reportsAPI.canonicalDefinition(reportId).then((res) => {
      if (!mounted) return;
      const def = res.data?.data || null;
      setDefinition(def);
      const saved = readSavedState(storageKey, tenantCode);
      setFilters(saved?.filters || def?.defaults?.filters || {});
      setSort(saved?.sort || def?.dataset?.defaultSort || []);
    }).catch((err) => {
      if (!mounted) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to load report definition');
    });
    return () => { mounted = false; };
  }, [reportId, tenantCode, refreshKey]);

  useEffect(() => {
    if (!definition) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ tenantCode: tenantCode || null, filters, sort }));
    } catch (_) {}
  }, [storageKey, definition, tenantCode, filters, sort]);

  useEffect(() => {
    if (!definition) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    reportsAPI.canonicalQuery(reportId, { filters, sort, page: { limit: 1000, offset: 0 } }).then((res) => {
      if (!mounted) return;
      const data = res.data?.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setFacets(data.facets || {});
      setTotal(Number(data.total || 0));
      setAggregateTotal(data.aggregate_total != null ? Number(data.aggregate_total) : null);
      setRunAt(new Date());
      setLoading(false);
    }).catch((err) => {
      if (!mounted) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to load report data');
      setRows([]);
      setFacets({});
      setTotal(0);
      setAggregateTotal(null);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [definition, reportId, filters, sort, refreshKey]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('.term-can-header-filter-wrap')) return;
      if (target && target.closest && target.closest('.term-can-filter-popover-portal')) return;
      setOpenFilterKey(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenFilterKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilterKey]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const updatePosition = () => {
      const button = filterButtonRefs.current[openFilterKey];
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const openColumn = columns.find((c) => c.key === openFilterKey);
      const isDateFilter = openColumn?.filter?.kind === 'dateRange';
      const isTerminationReasonFilter = openFilterKey === 'termination_reason';
      const width = isDateFilter ? 190 : (isTerminationReasonFilter ? 520 : 320);
      const viewportPadding = 8;
      const maxLeft = window.innerWidth - width - viewportPadding;
      const preferredLeft = rect.right + window.scrollX - width;
      const clampedLeft = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));
      setPopoverPosition({ top: rect.bottom + window.scrollY + 6, left: clampedLeft, width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openFilterKey, columns]);

  const activeFilterSummary = useMemo(
    () => columns.filter((c) => c.filter).map((col) => {
      const text = summarizeFilterValue(col, filters[col.key]);
      if (!text) return null;
      return `${col.label}: ${text}`;
    }).filter(Boolean),
    [columns, filters]
  );

  const closeDateRangeText = useMemo(() => {
    const range = filters?.close_date || {};
    const from = range.from ? formatDateShort(range.from) : 'Any';
    const to = range.to ? formatDateShort(range.to) : 'Any';
    if (!range.from && !range.to) return null;
    return `${from} to ${to}`;
  }, [filters]);

  const sortKey = sort?.[0]?.key;
  const sortDir = sort?.[0]?.dir;

  const handleSortClick = (key) => {
    setSort((prev) => {
      const current = Array.isArray(prev) && prev[0]?.key === key ? prev[0] : null;
      if (!current) return [{ key, dir: 'asc' }];
      if (current.dir === 'asc') return [{ key, dir: 'desc' }];
      return definition?.dataset?.defaultSort || [];
    });
  };

  const handleClearFilters = () => {
    setFilters(definition?.defaults?.filters || {});
    setSort(definition?.dataset?.defaultSort || []);
  };

  const handleCancelToReportsList = () => {
    const cat = definition?.toolbarLeadingAction?.category;
    /** Hub owns URL↔state sync; navigate() alone left stale `selectedReportId` and the sync effect re-added `?report=`. */
    if (reportsHubNav?.closeActiveReport) {
      reportsHubNav.closeActiveReport(cat || null);
      return;
    }
    if (cat) {
      navigate(`${tenantPath('/reports')}?category=${encodeURIComponent(cat)}`);
    } else {
      navigate(tenantPath('/reports'));
    }
  };

  const handleExport = async () => {
    try {
      const response = await reportsAPI.canonicalExportCsv(reportId, { filters, sort });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportId.replace(/-/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to export CSV');
    }
  };

  const handleCopyBcc = async () => {
    try {
      const res = await reportsAPI.canonicalSearchProvidersEmails({ filters });
      const emails = res.data?.emails || [];
      const text = emails.join('; ');
      if (text) {
        await navigator.clipboard.writeText(text);
        setBccMessage(`Copied ${emails.length} email(s) to clipboard`);
        setTimeout(() => setBccMessage(null), 2500);
      } else {
        setBccMessage('No emails for current filters');
        setTimeout(() => setBccMessage(null), 2500);
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to get email list');
    }
  };
  const handleOpenInEmail = async () => {
    try {
      const res = await reportsAPI.canonicalSearchProvidersEmails({ filters });
      const emails = res.data?.emails || [];
      if (emails.length === 0) {
        alert('No emails for current filters');
        return;
      }
      const maxBcc = 30;
      const bccList = emails.slice(0, maxBcc).join(';');
      const mailto = `mailto:?bcc=${encodeURIComponent(bccList)}`;
      window.open(mailto, '_blank');
      if (emails.length > maxBcc) {
        setBccMessage(`Opened email with first ${maxBcc} of ${emails.length} addresses. Copy full list for the rest.`);
        setTimeout(() => setBccMessage(null), 4000);
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to get email list');
    }
  };

  const renderFilterControl = (column) => {
    const kind = column?.filter?.kind;
    const value = draftFilters[column.key] || {};
    if (kind === 'textContains') {
      return (
        <input
          type="text"
          value={value.text || ''}
          onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { text: e.target.value } }))}
          placeholder={`Search ${column.label.toLowerCase()}`}
          className="term-can-filter-input"
        />
      );
    }
    if (kind === 'dateRange') {
      return (
        <div className="term-can-filter-date-range">
          <label className="term-can-filter-date-field">
            <span>From</span>
            <input
              type="date"
              value={value.from || ''}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, from: e.target.value } }))}
              className="term-can-filter-input"
            />
          </label>
          <label className="term-can-filter-date-field">
            <span>To</span>
            <input
              type="date"
              value={value.to || ''}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, to: e.target.value } }))}
              className="term-can-filter-input"
            />
          </label>
        </div>
      );
    }
    if (kind === 'boolean') {
      const checked = value?.value === true;
      return (
        <label className="term-can-filter-boolean">
          <input
            type="checkbox"
            checked={!!checked}
            onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { value: e.target.checked } }))}
          />
          <span>{column.label}</span>
        </label>
      );
    }
    if (kind === 'dateRangePresets') {
      const hasCustomDates = value.from || value.to;
      const preset = (value.preset || '').trim() || (hasCustomDates ? 'custom' : 'last_month');
      const isCustom = preset.toLowerCase() === 'custom';
      return (
        <div className="term-can-filter-date-presets">
          <select
            value={preset}
            onChange={(e) => {
              const nextPreset = e.target.value;
              setDraftFilters((prev) => ({
                ...prev,
                [column.key]: {
                  preset: nextPreset,
                  from: nextPreset === 'custom' ? (value.from || '') : '',
                  to: nextPreset === 'custom' ? (value.to || '') : '',
                },
              }));
            }}
            className="term-can-filter-input"
          >
            <option value="last_month">Last month</option>
            <option value="last_quarter">Last quarter</option>
            <option value="last_six_months">Last six months</option>
            <option value="last_year">Last year</option>
            <option value="custom">Custom</option>
          </select>
          {isCustom && (
            <div className="term-can-filter-date-range">
              <label className="term-can-filter-date-field">
                <span>From</span>
                <input
                  type="date"
                  value={value.from || ''}
                  onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, from: e.target.value } }))}
                  className="term-can-filter-input"
                />
              </label>
              <label className="term-can-filter-date-field">
                <span>To</span>
                <input
                  type="date"
                  value={value.to || ''}
                  onChange={(e) => setDraftFilters((prev) => ({ ...prev, [column.key]: { ...value, to: e.target.value } }))}
                  className="term-can-filter-input"
                />
              </label>
            </div>
          )}
        </div>
      );
    }
    if (kind === 'multiSelect') {
      const options = Array.isArray(facets[column.key]) ? facets[column.key] : [];
      const selectedValues = Array.isArray(value.values) ? value.values : [];
      const singleSelectKeys = ['staff_name', 'team'];
      const isSingleSelect = singleSelectKeys.includes(column.key);
      const getOptValue = (opt) => (opt != null && typeof opt === 'object' && 'value' in opt ? opt.value : opt);
      const getOptLabel = (opt) => (opt != null && typeof opt === 'object' && 'label' in opt ? opt.label : String(opt));
      if (isSingleSelect) {
        const selected = selectedValues[0] || '';
        return (
          <select
            value={selected}
            onChange={(e) => {
              const v = e.target.value;
              setDraftFilters((prev) => ({ ...prev, [column.key]: { values: v ? [v] : [] } }));
            }}
            className="term-can-filter-select term-can-filter-select-single"
            aria-label={`Select ${column.label}`}
          >
            <option value="">{column.key === 'staff_name' ? 'All staff' : 'All teams'}</option>
            {options.map((opt) => (
              <option key={getOptValue(opt)} value={getOptValue(opt)}>{getOptLabel(opt)}</option>
            ))}
          </select>
        );
      }
      const toggleDraftMulti = (optValue) => {
        setDraftFilters((prev) => {
          const current = Array.isArray(prev[column.key]?.values) ? prev[column.key].values : [];
          const next = current.includes(optValue) ? current.filter((v) => v !== optValue) : [...current, optValue];
          return { ...prev, [column.key]: { values: next } };
        });
      };
      return (
        <>
          <div className="term-can-filter-popover-actions term-can-filter-popover-actions-top">
            <button type="button" className="btn btn-secondary" onClick={() => clearFilter(column)}>Clear</button>
            <button type="button" className="btn btn-primary" onClick={() => applyFilter(column)}>Apply</button>
          </div>
          <div className="term-can-filter-multiselect-list" role="list">
            {options.map((opt) => {
              const optValue = getOptValue(opt);
              const optLabel = getOptLabel(opt);
              return (
                <label key={String(optValue)} className="term-can-filter-multiselect-check">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => toggleDraftMulti(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
        </>
      );
    }
    return null;
  };

  const openFilter = (key, buttonElement) => {
    setDraftFilters((prev) => ({ ...prev, [key]: filters[key] || {} }));
    if (buttonElement) filterButtonRefs.current[key] = buttonElement;
    setOpenFilterKey((prev) => (prev === key ? null : key));
  };

  const applyFilter = (column) => {
    const key = column.key;
    const kind = column.filter?.kind;
    const draft = draftFilters[key] || {};
    const nextFilters = { ...filters };
    if (kind === 'textContains') {
      const text = String(draft.text || '').trim();
      if (text) nextFilters[key] = { text };
      else delete nextFilters[key];
    } else if (kind === 'dateRange') {
      const from = draft.from || '';
      const to = draft.to || '';
      if (from || to) nextFilters[key] = { from, to };
      else delete nextFilters[key];
    } else if (kind === 'dateRangePresets') {
      const preset = (draft.preset || '').trim() || 'last_month';
      if (preset.toLowerCase() === 'custom') {
        const from = draft.from || '';
        const to = draft.to || '';
        if (from || to) nextFilters[key] = { preset: 'custom', from, to };
        else delete nextFilters[key];
      } else {
        nextFilters[key] = { preset };
      }
    } else if (kind === 'multiSelect') {
      const values = Array.isArray(draft.values) ? draft.values.filter(Boolean) : [];
      if (values.length > 0) nextFilters[key] = { values };
      else delete nextFilters[key];
    } else if (kind === 'boolean') {
      nextFilters[key] = { value: draft.value === true };
    }
    setFilters(nextFilters);
    setOpenFilterKey(null);
  };

  const clearFilter = (column) => {
    const key = column.key;
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDraftFilters((prev) => ({ ...prev, [key]: {} }));
    setOpenFilterKey(null);
  };

  const openColumn = visibleColumns.find((c) => c.key === openFilterKey);
  const filterPopover = openColumn
    ? createPortal(
        <div className="term-can-filter-popover term-can-filter-popover-portal" style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}>
          {renderFilterControl(openColumn)}
          <div className="term-can-filter-popover-actions">
            <button type="button" className="btn btn-secondary" onClick={() => clearFilter(openColumn)}>Clear</button>
            <button type="button" className="btn btn-primary" onClick={() => applyFilter(openColumn)}>Apply</button>
          </div>
        </div>,
        document.body
      )
    : null;

  const titleBase =
    definition?.toolbarTitle ??
    definition?.name ??
    (reportId === 'script-tasks-pending' ? 'Scripts - Pending Receipt' : 'Termination');
  const titleSuffix = tenantCode
    ? ` - ${reportId === 'script-tasks-pending' ? String(tenantCode).toUpperCase() : tenantCode}`
    : '';
  const runAtDisplay = runAt.toLocaleString();

  const toggleScriptRowExpand = (taskId) => {
    if (taskId == null) return;
    setExpandedScriptTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const formatCellValue = (col, raw) => {
    if (col.key === 'annual_due_ext') {
      return raw === 'X' ? 'X' : '';
    }
    if (col.key === 'close_date' && (raw == null || raw === '')) {
      return closeDateRangeText || '—';
    }
    if (raw == null || raw === '') return '—';
    if (col.type === 'date') return formatDateShort(raw);
    if (col.key === 'pct_total') return `${Number(raw).toFixed(1)}%`;
    if (typeof raw === 'number') return raw.toLocaleString();
    return raw;
  };

  return (
    <>
      <div className={`term-can-report ${embedded ? 'term-can-embedded' : ''}`}>
        <div className="term-can-toolbar no-print">
          <div className="term-can-toolbar-left">
            <div className="term-can-title-row">
              <h3 className="term-can-title">{titleBase}{titleSuffix}</h3>
              {definition?.toolbarShowDescriptionInfo && definition?.description ? (
                <InfoIconButton
                  onClick={() => setDescriptionOpen((v) => !v)}
                  ariaExpanded={descriptionOpen}
                  ariaControls="term-can-report-description"
                  ariaLabel="Show report description"
                  ariaLabelExpanded="Hide report description"
                  className="term-can-title-info"
                />
              ) : null}
            </div>
            {definition?.toolbarShowDescriptionInfo && descriptionOpen && definition?.description ? (
              <p id="term-can-report-description" className="term-can-toolbar-description">
                {definition.description}
              </p>
            ) : null}
            <div className="term-can-subtitle">
              {loading
                ? 'Loading...'
                : aggregateTotal != null
                  ? `${total} rows (${aggregateTotal} referrals)`
                  : `${total} row${total === 1 ? '' : 's'}`}
            </div>
          </div>
          <div className="term-can-toolbar-actions">
            {reportId === 'search-providers' && (
              <>
                <button type="button" className="btn btn-outline" onClick={handleCopyBcc}>Copy BCC list</button>
                <button type="button" className="btn btn-outline" onClick={handleOpenInEmail}>Open in email</button>
                {bccMessage && <span className="term-can-bcc-message">{bccMessage}</span>}
              </>
            )}
            {definition?.toolbarLeadingAction?.type === 'navigateBackToReports' ? (
              <button type="button" className="btn btn-outline" onClick={handleCancelToReportsList}>Cancel</button>
            ) : (
              <button type="button" className="btn btn-outline" onClick={handleClearFilters}>Reset</button>
            )}
            <button type="button" className="btn btn-primary" onClick={handleExport}>Export CSV</button>
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>Print</button>
          </div>
        </div>
        {activeFilterSummary.length > 0 && <p className="term-can-filter-summary no-print">Filters: {activeFilterSummary.join(' | ')}</p>}
        {filtersAboveTable.length > 0 && (
          <div className="term-can-filters-above no-print">
            {filtersAboveTable.map((key) => {
              const col = columns.find((c) => c.key === key);
              if (!col?.filter) return null;
              const value = filters[key] || {};
              if (col.filter.kind === 'dateRange') {
                const from = value.from || value.to || '';
                return (
                  <div key={key} className="term-can-filter-inline">
                    <label className="term-can-filter-inline-label">Due by</label>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => {
                        const d = e.target.value;
                        setFilters((prev) => ({ ...prev, [key]: { from: d, to: d } }));
                      }}
                      className="term-can-filter-input"
                    />
                  </div>
                );
              }
              if (col.filter.kind === 'boolean') {
                const checked = value?.value === true;
                return (
                  <div key={key} className="term-can-filter-inline">
                    <label className="term-can-filter-inline-check">
                      <input
                        type="checkbox"
                        checked={!!checked}
                        onChange={(e) => setFilters((prev) => ({ ...prev, [key]: { value: e.target.checked } }))}
                      />
                      <span>{col.label || 'Filter'}</span>
                    </label>
                  </div>
                );
              }
              if (col.filter.kind === 'multiSelect') {
                const options = Array.isArray(facets[key]) ? facets[key] : [];
                const selected = Array.isArray(value.values) ? value.values[0] : '';
                return (
                  <div key={key} className="term-can-filter-inline">
                    <label className="term-can-filter-inline-label">Staff</label>
                    <select
                      value={selected}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters((prev) => ({ ...prev, [key]: { values: v ? [v] : [] } }));
                      }}
                      className="term-can-filter-input term-can-filter-select-inline"
                    >
                      <option value="">All staff</option>
                      {options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
        <div className="term-can-print-header print-only"><h1>{titleBase}{titleSuffix}</h1></div>
        {error && <div className="term-can-error">{error}</div>}
        <div className="term-can-table-wrap">
          <table className="term-can-table">
            <thead>
              <tr>
                {isScriptPending && (
                  <th className="term-can-expand-col no-print" scope="col" aria-label="Expand row" />
                )}
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && handleSortClick(col.key)}
                    title={col.key === 'annual_due_ext' ? (col.description || undefined) : undefined}
                    aria-label={col.key === 'annual_due_ext' ? (col.description || 'Extended annual due') : undefined}
                    className={`${col.sortable ? 'term-can-sortable' : ''} ${numericColumnKeys.has(col.key) ? 'term-can-cell-num' : ''} ${col.narrow ? 'term-can-th-narrow-wrap' : ''} ${col.hideInPrint ? 'term-can-col-hide-print' : ''} ${col.key === 'annual_due_ext' ? 'term-can-th-ext-marker' : ''}`.trim()}
                  >
                    <div className="term-can-header-cell">
                      <span className="term-can-header-label">
                        {col.key === 'annual_due_ext' ? '\u2007' : col.label}
                        {sortKey === col.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </span>
                      {col.filter && !filtersAboveTable.includes(col.key) && (
                        <div className="term-can-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`term-can-filter-trigger ${isFilterActive(col, filters[col.key]) ? 'is-active' : ''}`}
                            onClick={(e) => openFilter(col.key, e.currentTarget)}
                            aria-label={`Filter ${col.label || col.description || col.key}`}
                          >
                            <svg className="term-can-filter-trigger-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={scriptPendingColSpan}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={scriptPendingColSpan}>No rows match the current filters.</td></tr>
              ) : (
                rows.map((row, idx) => {
                  const rowKey = row.task_id || row.referral_id || `${row.reason_code || 'row'}-${idx}`;
                  const taskId = row.task_id;
                  const expanded = isScriptPending && taskId != null && expandedScriptTaskIds.has(taskId);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr>
                        {isScriptPending && (
                          <td className="term-can-expand-col no-print">
                            <button
                              type="button"
                              className="term-can-row-expand-btn"
                              onClick={() => toggleScriptRowExpand(taskId)}
                              aria-expanded={expanded}
                              aria-controls={taskId != null ? `script-pending-detail-${taskId}` : undefined}
                              aria-label={expanded ? 'Hide task and work type' : 'Show task and work type'}
                            >
                              <span className="term-can-row-expand-icon" aria-hidden>
                                {expanded ? '▼' : '▶'}
                              </span>
                            </button>
                          </td>
                        )}
                        {visibleColumns.map((col) => {
                          const raw = row[col.key];
                          let display = formatCellValue(col, raw);
                          if (isScriptPending && col.linkToClient && col.key === 'client_name' && row.client_id) {
                            const text = raw == null || raw === '' ? '—' : String(raw);
                            display = (
                              <Link className="term-can-client-link" to={tenantPath(`/client/${row.client_id}`)}>
                                {text}
                              </Link>
                            );
                          }
                          const wrapClass = col.wrap ? (col.narrow ? ' term-can-cell-narrow-wrap' : ' term-can-cell-wrap') : (col.key === 'referral_reason' ? ' term-can-cell-wrap' : '');
                          const numClass = numericColumnKeys.has(col.key) ? ' term-can-cell-num' : '';
                          const hidePrintClass = col.hideInPrint ? ' term-can-col-hide-print' : '';
                          const extMarkerClass = col.key === 'annual_due_ext' ? ' term-can-td-ext-marker' : '';
                          return (
                            <td key={`${idx}-${col.key}`} className={`${numClass}${wrapClass}${hidePrintClass}${extMarkerClass}`.trim() || undefined}>
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                      {isScriptPending && expanded && taskId != null && (
                        <tr className="term-can-detail-row no-print" aria-hidden={false}>
                          <td
                            colSpan={scriptPendingColSpan}
                            className="term-can-script-pending-detail"
                            id={`script-pending-detail-${taskId}`}
                            style={{ maxWidth: 0 }}
                          >
                            <div className="term-can-script-pending-detail-inner">
                              <span className="term-can-script-pending-detail-item">
                                <strong>Task:</strong> {row.task_type_name || '—'}
                              </span>
                              <span className="term-can-script-pending-detail-item">
                                <strong>Work type:</strong> {row.work_type_name || '—'}
                              </span>
                            </div>
                            <div className="term-can-script-pending-detail-line2">
                              <strong>Request date:</strong>{' '}
                              {row.requested_date != null && row.requested_date !== ''
                                ? formatDateShort(row.requested_date)
                                : '—'}
                            </div>
                            <div className="term-can-script-pending-detail-line2">
                              <strong>Note:</strong> {row.note || '—'}
                            </div>
                            {row.client_id && row.referral_id && (
                              <div className="term-can-script-pending-detail-line2">
                                <Link
                                  to={`${tenantPath(`/client/${row.client_id}/work-tasks`)}?referralId=${encodeURIComponent(row.referral_id)}&taskId=${encodeURIComponent(taskId)}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Open script →
                                </Link>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="term-can-print-footer print-only">
          <p>Run at: {runAtDisplay}</p>
          <p>Filters: {activeFilterSummary.length > 0 ? activeFilterSummary.join(' | ') : 'None'}</p>
        </div>
      </div>
      {filterPopover}
    </>
  );
}

export default TerminationCanonicalReport;
